/* src/components/customGame/player/CustomGameResults.js */
import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRedo, faCog, faThLarge, faChartBar } from '@fortawesome/free-solid-svg-icons';

// Helper component for displaying individual stats
const StatCard = ({ label, value, unit = "", icon, helpText = "" }) => (
    <div className="bg-gray-50 p-4 rounded-lg shadow border border-gray-200 flex flex-col items-center justify-center text-center min-h-[100px]">
        {icon && <FontAwesomeIcon icon={icon} className="text-2xl text-indigo-500 mb-2" />}
        <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
        <div className="text-2xl font-bold text-gray-800 mt-1">
            {value !== undefined && value !== null ? value : '-'}
            {value !== undefined && value !== null && unit && <span className="text-lg ml-1">{unit}</span>}
        </div>
        {helpText && <p className="text-xs text-gray-400 mt-1">{helpText}</p>}
    </div>
);

StatCard.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    unit: PropTypes.string,
    icon: PropTypes.object, // FontAwesome icon object
    helpText: PropTypes.string,
};


const CustomGameResults = ({
    gameResults, // Object containing performance metrics
    config,      // The configuration of the game that was just played
    onRetry,
    onConfigureNew,
    onGoToDashboard,
}) => {

    // Fallback for gameResults if it's null/undefined
    const results = gameResults || {};
    const presetName = config?.presetMetadata?.name || "Your Custom Game";
    const nValueDisplay = config?.settings?.Session?.NValue?.Mode === 'Fixed'
        ? config.settings.Session.NValue.FixedValue
        : (config?.settings?.Session?.NValue?.Mode || 'N/A');

    // Extract common metrics with fallbacks
    const accuracy = results.accuracy !== undefined ? (results.accuracy * 100).toFixed(1) : null;
    const meanRT = results.meanReactionTime !== undefined ? Math.round(results.meanReactionTime) : null;
    const dPrime = results.dPrime !== undefined ? results.dPrime.toFixed(2) : null;
    const totalTrialsCompleted = results.trialsCompleted || config?.settings?.Session?.Termination?.TrialCount || 0;

    // Determine if any results are available to display meaningfully
    const hasMeaningfulResults = accuracy !== null || meanRT !== null || dPrime !== null;

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-2xl w-full max-w-2xl text-center border border-gray-200">
                <FontAwesomeIcon icon={faChartBar} className="text-4xl text-indigo-500 mb-4" />
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
                    {hasMeaningfulResults ? "Custom Game Complete!" : "Session Ended"}
                </h1>
                <p className="text-sm text-gray-600 mb-1">
                    Configuration: <span className="font-semibold">{presetName}</span>
                </p>
                <p className="text-sm text-gray-500 mb-6">
                    N-Level: <span className="font-semibold">{nValueDisplay}</span>
                    {totalTrialsCompleted > 0 && `  |  Trials Attempted: ${results.trialsAttempted || totalTrialsCompleted}`}
                </p>

                {hasMeaningfulResults ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                        {accuracy !== null && (
                            <StatCard label="Accuracy" value={accuracy} unit="%" helpText="Overall correctness" />
                        )}
                        {meanRT !== null && (
                            <StatCard label="Avg. Reaction Time" value={meanRT} unit="ms" helpText="For correct matches" />
                        )}
                        {dPrime !== null && (
                            <StatCard label="d' (Sensitivity)" value={dPrime} helpText="Ability to discriminate targets" />
                        )}
                        {/* Add more StatCards here for other metrics from gameResults as they become available */}
                        {/* e.g., Misses, False Alarms */}
                         {(results.misses !== undefined || results.falseAlarms !== undefined) && !dPrime && ( // Show if d' not available but these are
                            <>
                                {results.misses !== undefined && <StatCard label="Misses" value={results.misses} />}
                                {results.falseAlarms !== undefined && <StatCard label="False Alarms" value={results.falseAlarms} />}
                            </>
                        )}
                    </div>
                ) : (
                    <div className="my-8 p-4 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-700">
                        <p>No detailed performance metrics available for this session.</p>
                        {results.message && <p className="text-xs mt-1">{results.message}</p>}
                    </div>
                )}
                
                {/* Display per-stimulus results if available (more advanced) */}
                {results.perStimulusStats && Object.keys(results.perStimulusStats).length > 0 && (
                    <div className="my-6 text-left">
                        <h3 className="text-md font-semibold text-gray-700 mb-2">Performance by Stimulus:</h3>
                        <div className="space-y-2 text-xs">
                            {Object.entries(results.perStimulusStats).map(([key, stats]) => (
                                <div key={key} className="p-2 bg-gray-50 rounded border border-gray-200">
                                    <span className="font-medium text-gray-600">{key.charAt(0).toUpperCase() + key.slice(1)}: </span>
                                    Accuracy: {(stats.accuracy * 100).toFixed(0)}%
                                    {stats.dPrime !== undefined && `, d': ${stats.dPrime.toFixed(2)}`}
                                </div>
                            ))}
                        </div>
                    </div>
                )}


                <div className="mt-8 pt-6 border-t border-gray-200 flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4">
                    <button
                        onClick={onRetry}
                        className="w-full sm:w-auto px-5 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors flex items-center justify-center text-sm"
                    >
                        <FontAwesomeIcon icon={faRedo} className="mr-2" />
                        Retry This Configuration
                    </button>
                    <button
                        onClick={onConfigureNew}
                        className="w-full sm:w-auto px-5 py-2.5 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors flex items-center justify-center text-sm"
                    >
                        <FontAwesomeIcon icon={faCog} className="mr-2" />
                        Configure New Game
                    </button>
                    <button
                        onClick={onGoToDashboard}
                        className="w-full sm:w-auto px-5 py-2.5 bg-white text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors flex items-center justify-center text-sm"
                    >
                        <FontAwesomeIcon icon={faThLarge} className="mr-2" />
                        Back to Dashboard
                    </button>
                </div>
            </div>
            <FeedbackButton /> {/* Standard feedback button */}
        </div>
    );
};

CustomGameResults.propTypes = {
    gameResults: PropTypes.shape({
        accuracy: PropTypes.number,
        meanReactionTime: PropTypes.number,
        dPrime: PropTypes.number,
        trialsCompleted: PropTypes.number,
        trialsAttempted: PropTypes.number, // How many trials the user actually saw/responded to
        misses: PropTypes.number,
        falseAlarms: PropTypes.number,
        perStimulusStats: PropTypes.object, // E.g., { audio: { accuracy: 0.9, dPrime: 2.1 }, position: { ... } }
        message: PropTypes.string, // Optional message if game ended early etc.
        // Add other expected metrics here
    }), // Can be null if game ended prematurely with no results
    config: PropTypes.object.isRequired, // The configuration object of the game played
    onRetry: PropTypes.func.isRequired,
    onConfigureNew: PropTypes.func.isRequired,
    onGoToDashboard: PropTypes.func.isRequired,
};

export default CustomGameResults;